// Cem Yuksel's HAIR file format (cemyuksel.com/research/hairmodels): the public
// strand models the hair test scene renders. Pure: bytes in, typed arrays out.
//
// Layout (little-endian): a 128-byte header — "HAIR", hair_count u32,
// point_count u32, arrays bitfield u32 (1 segments, 2 points, 4 thickness,
// 8 transparency, 16 color), d_segments u32, d_thickness f32,
// d_transparency f32, d_color 3×f32, 88 bytes of info — then the arrays that
// the bitfield says are present, in that order: segments (u16 per strand),
// points (3×f32 per point), thickness (f32 per point), transparency (f32 per
// point), color (3×f32 per point). A strand with N segments has N+1 points and
// the points are stored strand after strand.

export const HAIR_ARRAYS = Object.freeze({ segments: 1, points: 2, thickness: 4, transparency: 8, color: 16 })

/**
 * Parses a .hair buffer. Returns { strandCount, pointCount, segments (Uint16Array
 * or null when every strand has defaultSegments), points (Float32Array xyz),
 * defaultSegments, defaultThickness, defaultColor:[r,g,b], info }.
 * Throws on a bad signature or a truncated file — a short read must never be
 * rendered as a thin head of hair.
 */
export function parseHairFile(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? buffer : buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength)
  if (bytes.byteLength < 128) throw new Error(`hairFormat: ${bytes.byteLength} bytes is shorter than the 128-byte header`)
  const view = new DataView(bytes)
  const sig = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3))
  if (sig !== 'HAIR') throw new Error(`hairFormat: signature "${sig}" is not "HAIR"`)
  const strandCount = view.getUint32(4, true)
  const pointCount = view.getUint32(8, true)
  const arrays = view.getUint32(12, true)
  const defaultSegments = view.getUint32(16, true)
  const defaultThickness = view.getFloat32(20, true)
  const defaultColor = [view.getFloat32(28, true), view.getFloat32(32, true), view.getFloat32(36, true)]
  let info = ''
  for (let i = 40; i < 128; i++) { const c = view.getUint8(i); if (c === 0) break; info += String.fromCharCode(c) }
  if (!(arrays & HAIR_ARRAYS.points)) throw new Error('hairFormat: file carries no points array')

  let offset = 128
  const need = (n, what) => {
    if (offset + n > bytes.byteLength) throw new Error(`hairFormat: truncated — ${what} needs ${n} bytes at ${offset}, file has ${bytes.byteLength}`)
  }
  let segments = null
  if (arrays & HAIR_ARRAYS.segments) {
    need(strandCount * 2, 'segments')
    segments = new Uint16Array(strandCount)
    for (let i = 0; i < strandCount; i++) segments[i] = view.getUint16(offset + i * 2, true)
    offset += strandCount * 2
  }
  need(pointCount * 12, 'points')
  const points = new Float32Array(pointCount * 3)
  for (let i = 0; i < pointCount * 3; i++) points[i] = view.getFloat32(offset + i * 4, true)
  offset += pointCount * 12
  // Thickness / transparency / per-point colour are not used by the ribbon
  // renderer (width is a screen-space knob, colour a material parameter);
  // their presence is validated so a truncated file is still refused.
  if (arrays & HAIR_ARRAYS.thickness) { need(pointCount * 4, 'thickness'); offset += pointCount * 4 }
  if (arrays & HAIR_ARRAYS.transparency) { need(pointCount * 4, 'transparency'); offset += pointCount * 4 }
  if (arrays & HAIR_ARRAYS.color) { need(pointCount * 12, 'color') }

  let total = 0
  if (segments) for (let i = 0; i < strandCount; i++) total += segments[i] + 1
  else total = strandCount * (defaultSegments + 1)
  if (total !== pointCount) throw new Error(`hairFormat: strand segments sum to ${total} points but the header says ${pointCount}`)

  return { strandCount, pointCount, segments, points, defaultSegments, defaultThickness, defaultColor, info }
}

/**
 * Per-SEGMENT buffers for LineSegmentsGeometry from a parsed hair file:
 * positions (start xyz, end xyz) and params (uStart, uEnd, seed, 0), the same
 * layout hairStrands.js's segmentBuffers produces for a glTF LINES primitive.
 * `stride` keeps every Nth strand (uniform thinning, so the silhouette is
 * kept), `scale` converts the file's units, and `flipYZ` swaps Y and Z for a
 * Z-up model. Returns { positions, params, segments, strands, roots } where
 * roots is one xyz per kept strand (its first point), for framing.
 */
export function hairToSegmentBuffers(hair, { stride = 1, scale = 1, flipYZ = false } = {}) {
  const step = Math.max(1, Math.floor(stride))
  const { strandCount, segments, points, defaultSegments } = hair
  let segTotal = 0, kept = 0
  for (let s = 0; s < strandCount; s += step) { segTotal += segments ? segments[s] : defaultSegments; kept++ }
  const positions = new Float32Array(segTotal * 6)
  const params = new Float32Array(segTotal * 4)
  const roots = new Float32Array(kept * 3)
  let pointBase = 0, w = 0, k = 0
  for (let s = 0; s < strandCount; s++) {
    const n = segments ? segments[s] : defaultSegments
    if (s % step === 0) {
      const seed = strandSeed(s)
      for (let i = 0; i < n; i++) {
        const a = (pointBase + i) * 3, b = a + 3
        if (flipYZ) {
          positions[w * 6 + 0] = points[a] * scale; positions[w * 6 + 1] = points[a + 2] * scale; positions[w * 6 + 2] = points[a + 1] * scale
          positions[w * 6 + 3] = points[b] * scale; positions[w * 6 + 4] = points[b + 2] * scale; positions[w * 6 + 5] = points[b + 1] * scale
        } else {
          positions[w * 6 + 0] = points[a] * scale; positions[w * 6 + 1] = points[a + 1] * scale; positions[w * 6 + 2] = points[a + 2] * scale
          positions[w * 6 + 3] = points[b] * scale; positions[w * 6 + 4] = points[b + 1] * scale; positions[w * 6 + 5] = points[b + 2] * scale
        }
        params[w * 4 + 0] = i / n
        params[w * 4 + 1] = (i + 1) / n
        params[w * 4 + 2] = seed
        w++
      }
      roots[k * 3] = positions[(w - n) * 6]; roots[k * 3 + 1] = positions[(w - n) * 6 + 1]; roots[k * 3 + 2] = positions[(w - n) * 6 + 2]
      k++
    }
    pointBase += n + 1
  }
  return { positions, params, segments: segTotal, strands: kept, roots }
}

/** A stable per-strand random in 0..1 from its index (the plugin uses the same hash). */
export function strandSeed(index) {
  let h = (index * 2654435761) >>> 0
  h ^= h >>> 15
  h = (h * 2246822519) >>> 0
  h ^= h >>> 13
  return (h & 0xffff) / 65535
}
